import * as THREE from 'three';

// ── Harbinger summoning circle ──────────────────────────────────
//
// AAA-grade ground portal. Fully procedural. The visible portal is
// SHRUNK to fit inside the square (~85% of the cell half-extent) so
// none of the circle gets clipped at the cell corners. Within that
// envelope:
//
//   1. Inner portal swirl. Inigo Quilez recursive domain-warped
//      FBM gives the interior an organic, swirling void feel.
//      Reference: https://iquilezles.org/articles/warp/
//   2. Embers. Sparser hot specks drifting through the interior
//      driven by a second warp.
//   3. Sigil. Six-pointed star (two interlocking triangle SDFs),
//      slow counter-rotating, pulsing scale.
//   4. Inner / outer rune rings. Dashed bands with per-cell hash
//      drop-outs and brightness variation, eroded by FBM. The
//      WHOLE polar field is perturbed by noise so the rings wobble
//      organically and the dashes spread irregularly. No clean
//      "step(fract(angle*N))" stripes that read as UI.
//   5. Aetheric smoke. Polar warped FBM at the rim, spilling
//      outward into the square but kept inside the cell.
//   6. Halo. Soft radial falloff so the portal radiates light.
//
// Three drive uniforms:
//   uActive (0..1). 1 while the once-per-turn discount is
//                       available, lerped to 0.35 once consumed.
//   uSpawn  (0..1). Birth ramp (easeOutBack scale + alpha).
//   uPlayFlash (0..1). Surge driven by GameBoard when a minion is
//                       actually summoned onto this marked cell.
//                       Boosts brightness, alpha, and the sigil's
//                       core glow for ~600ms before decaying.
//
// renderOrder = -90 keeps the decal above the playmat (Y=0.05/0.06)
// and below cards (Y≈0.075+). polygonOffset prevents z-fighting
// with the layered UI overlay.

const DECAL_Y = 0.07;

const PALETTES = {
  p1: {
    inner: new THREE.Color(0xffd070),
    mid:   new THREE.Color(0xb56828),
    outer: new THREE.Color(0x3a1606),
    glow:  new THREE.Color(0xfff0c0),
  },
  p2: {
    inner: new THREE.Color(0xff9070),
    mid:   new THREE.Color(0xa83a30),
    outer: new THREE.Color(0x40100a),
    glow:  new THREE.Color(0xffd0bc),
  },
  // Dragon portals (Caelestis's free-cast beacon, Dragonlord's Lair).
  // Deep emerald with a teal glow. Same shader, unmistakably NOT a
  // Harbinger mark (gold / red), so the two cell-discount systems
  // never read as the same effect on the board.
  dragon: {
    inner: new THREE.Color(0x7dffc8),
    mid:   new THREE.Color(0x1e9e6a),
    outer: new THREE.Color(0x06301e),
    glow:  new THREE.Color(0xb8ffe0),
  },
};

const VERT = /* glsl */`
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG = /* glsl */`
  precision highp float;

  uniform float uTown;
  uniform float uTime;
  uniform float uActive;
  uniform float uSpawn;
  uniform float uPlayFlash;
  uniform vec3  uColorInner;
  uniform vec3  uColorMid;
  uniform vec3  uColorOuter;
  uniform vec3  uColorGlow;

  varying vec2 vUv;

  // ── Noise utilities ────────────────────────────────────────────
  float hash21(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash21(i),                  hash21(i + vec2(1.0, 0.0)), u.x),
      mix(hash21(i + vec2(0.0, 1.0)), hash21(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }
  float fbm(vec2 p) {
    float v = 0.0;
    float a = 0.55;
    for (int i = 0; i < 5; i++) {
      v += a * vnoise(p);
      p = p * 2.02 + vec2(3.7, 1.9);
      a *= 0.5;
    }
    return v;
  }
  // Inigo Quilez recursive domain warping for organic, smoky swirls.
  float warpedFbm(vec2 p) {
    vec2 q = vec2(fbm(p), fbm(p + vec2(5.2, 1.3)));
    vec2 r = vec2(
      fbm(p + 4.0 * q + vec2(1.7, 9.2)),
      fbm(p + 4.0 * q + vec2(8.3, 2.8))
    );
    return fbm(p + 4.0 * r);
  }

  float sdStar(vec2 p, float r, int n, float m) {
    float an = 3.141593 / float(n);
    float en = 3.141593 / m;
    vec2  acs = vec2(cos(an), sin(an));
    vec2  ecs = vec2(cos(en), sin(en));
    float bn  = mod(atan(p.x, p.y), 2.0 * an) - an;
    p = length(p) * vec2(cos(bn), abs(sin(bn)));
    p -= r * acs;
    p += ecs * clamp(-dot(p, ecs), 0.0, r * acs.y / ecs.y);
    return length(p) * sign(p.x);
  }

  vec2 rot2(vec2 p, float a) {
    float c = cos(a), s = sin(a);
    return mat2(c, -s, s, c) * p;
  }

  void main() {
    if (uTown > 0.5) {
      // A quiet square boundary for the town, distinct from summoning portals.
      vec2 q = abs(vUv * 2.0 - 1.0);
      float edge = max(q.x, q.y);
      float border = smoothstep(0.82, 0.86, edge) * (1.0 - smoothstep(0.89, 0.94, edge));
      gl_FragColor = vec4(0.48, 0.72, 1.0, border * uActive * 0.7);
      return;
    }
    // Center coords in [-1, 1]. Then expand by 1/0.85 so the portal
    // fits comfortably inside the cell square. The visible content
    // is hard-capped at r=1.0 in the new coords (≈0.85 of original
    // cell half-extent), leaving margin so the cell corners never
    // crop the circle.
    vec2 pRaw = vUv * 2.0 - 1.0;
    vec2 p = pRaw / 0.85;

    // ── Spawn ramp (easeOutBack scale + alpha) ─────────────────
    float spawnS = clamp(uSpawn, 0.0, 1.0);
    float spawnD = spawnS - 1.0;
    float spawnEase = 1.0 + 2.7 * spawnD * spawnD * spawnD + 1.7 * spawnD * spawnD;
    float spawnScale = max(spawnEase, 0.001);
    p /= spawnScale;

    float r = length(p);
    if (r > 1.02) discard;

    // ── Organic polar distortion ────────────────────────────────
    // Pull the polar (ang, r) coords through FBM so EVERY downstream
    // layer (rings, runes, smoke) wobbles non-circularly. This is
    // what kills the "perfect concentric UI" look. The displacement
    // animates slowly via uTime so the wobble lives.
    float warpAng = (fbm(p * 1.6 + vec2(uTime * 0.04, 0.0)) - 0.5) * 0.16;
    float warpR   = (fbm(p * 2.1 + vec2(uTime * 0.03, 7.3)) - 0.5) * 0.07;
    float angRaw  = atan(p.y, p.x);
    float ang     = angRaw + warpAng;
    float rW      = r * (1.0 + warpR);

    vec3 col = vec3(0.0);

    // ── 1. Inner portal swirl ─────────────────────────────────
    vec2 warpUv = p * 1.7 + vec2(uTime * 0.045, 0.0);
    float swirl = warpedFbm(warpUv);
    float portalMask = 1.0 - smoothstep(0.50, 0.66, rW);
    vec3 portalCol = mix(vec3(0.015, 0.005, 0.0), uColorOuter, smoothstep(0.20, 0.55, swirl));
    portalCol = mix(portalCol, uColorMid, smoothstep(0.50, 0.80, swirl));
    portalCol += uColorGlow * pow(smoothstep(0.70, 1.0, swirl), 2.0) * 0.5;
    col += portalCol * portalMask;

    // ── 2. Embers ─────────────────────────────────────────────
    vec2 emberUv = p * 4.5 + vec2(0.0, -uTime * 0.18);
    float emberN = warpedFbm(emberUv);
    float emberMask = smoothstep(0.88, 1.0, emberN) * (1.0 - smoothstep(0.43, 0.62, rW));
    col += uColorGlow * emberMask * 1.4;

    // ── 3. Sigil (six-pointed star) ──────────────────────────
    float sigilPulse = 0.94 + 0.06 * sin(uTime * 1.4);
    vec2 sp = p / sigilPulse;
    float starA = sdStar(rot2(sp,  uTime * 0.05 + 0.0),  0.36, 3, 3.0);
    float starB = sdStar(rot2(sp, -uTime * 0.05 + 1.05), 0.36, 3, 3.0);
    float sigilSdf = min(starA, starB);
    float sigilLine = smoothstep(0.012, 0.0, abs(sigilSdf + 0.007));
    float sigilHalo = smoothstep(0.16, -0.03, sigilSdf) * 0.30;
    col += uColorGlow * sigilHalo;
    col += uColorInner * sigilLine * 1.6;

    // ── 4. Inner rune ring ────────────────────────────────────
    // Dash phase carries an FBM-driven offset so the spacing isn't
    // uniform. Per-cell hash drops some dashes and varies bright-
    // ness; a second FBM erodes the band's alpha. Combined the ring
    // reads as carved glyphs, not a stamped UI bar.
    float innerAngB = ang - uTime * 0.15;
    float innerDashJitter = (fbm(p * 4.5 + vec2(0.0, uTime * 0.05)) - 0.5) * 0.55;
    float innerBand = smoothstep(0.46, 0.49, rW) * (1.0 - smoothstep(0.57, 0.60, rW));
    float innerDashPhase = (innerAngB + innerDashJitter) * (18.0 / 6.2832);
    float innerDash = step(0.46, abs(fract(innerDashPhase) - 0.5) * 2.0);
    float innerCellHash = hash21(vec2(floor(innerDashPhase), 17.0));
    float innerKeep = step(0.28, innerCellHash);
    float innerBright = 0.55 + 0.45 * innerCellHash;
    float innerErode = smoothstep(0.36, 0.66, fbm(p * 7.0 + vec2(uTime * 0.04, 0.0)));
    float runeInner = innerBand * innerDash * innerKeep * (0.55 + 0.45 * innerErode);
    col += uColorInner * runeInner * innerBright * 1.15;

    // ── 5. Outer rune ring ────────────────────────────────────
    float outerAng = ang + uTime * 0.085;
    float outerDashJitter = (fbm(p * 3.5 + vec2(uTime * 0.04, 3.1)) - 0.5) * 0.45;
    float outerBand = smoothstep(0.66, 0.675, rW) * (1.0 - smoothstep(0.77, 0.785, rW));
    float outerDashPhase = (outerAng + outerDashJitter) * (13.0 / 6.2832);
    float outerDash = step(0.52, abs(fract(outerDashPhase) - 0.5) * 2.0);
    float outerCellHash = hash21(vec2(floor(outerDashPhase), 7.0));
    float outerKeep = step(0.22, outerCellHash);
    float outerBright = 0.50 + 0.50 * outerCellHash;
    float outerErode = smoothstep(0.35, 0.70, fbm(p * 4.5 + vec2(-uTime * 0.035, 1.5)));
    float runeOuter = outerBand * outerDash * outerKeep * (0.50 + 0.50 * outerErode);
    col += uColorMid * runeOuter * outerBright * 1.35;

    // Thin solid ring bounds with noise-modulated alpha so they
    // don't read as perfect circles. The same polar warp shifts
    // them off-circle, and an FBM mask softly breaks them up.
    float ringNoiseInner = fbm(vec2(ang * 4.0, uTime * 0.05));
    float ringNoiseOuter = fbm(vec2(ang * 3.0 + 5.7, uTime * 0.04));
    float ringInnerEdge = (1.0 - smoothstep(0.0, 0.006, abs(rW - 0.675)))
                         * (0.55 + 0.45 * ringNoiseInner);
    float ringOuterEdge = (1.0 - smoothstep(0.0, 0.006, abs(rW - 0.77)))
                         * (0.45 + 0.55 * ringNoiseOuter);
    col += uColorMid * (ringInnerEdge + ringOuterEdge) * 0.85;

    // ── 6. Aetheric smoke at the rim ─────────────────────────
    // Polar warped FBM. Sits at r ≈ 0.78-0.92 so the smoke fades
    // out inside the cell square, not at the corners.
    vec2 polar = vec2(ang * 1.5 + uTime * 0.06, rW * 4.0);
    float smoke = warpedFbm(polar);
    float smokeMask = smoothstep(0.78, 0.90, rW) * (1.0 - smoothstep(0.90, 1.00, rW));
    col += uColorMid * smoke * smokeMask * 0.95;
    col += uColorGlow * pow(smoke, 3.0) * smokeMask * 0.5;

    // ── 7. Outer halo ────────────────────────────────────────
    float halo = smoothstep(1.00, 0.35, rW) * 0.14;
    col += uColorGlow * halo;

    // ── 8. Play-flash surge ──────────────────────────────────
    // GameBoard sets uPlayFlash → 1.0 the instant a minion is
    // summoned onto this marked cell; it decays to 0 over ~600ms
    // (orchestrator-driven). The surge biases the brightness, the
    // sigil halo, and the inner glow without changing layer
    // geometry. The portal stays in place but ignites.
    float flash = clamp(uPlayFlash, 0.0, 1.0);
    if (flash > 0.001) {
      float flashCore = smoothstep(0.50, 0.0, rW);
      col += uColorGlow * flashCore * flash * 1.2;
      col += uColorInner * (sigilLine + sigilHalo) * flash * 0.9;
      col += uColorGlow * (runeInner + runeOuter) * flash * 0.6;
    }

    // ── Composite alpha ──────────────────────────────────────
    float a = portalMask * 0.85
            + emberMask * 1.6
            + sigilLine * 0.95
            + sigilHalo * 0.55
            + runeInner * innerBright * 1.10
            + runeOuter * outerBright * 1.05
            + (ringInnerEdge + ringOuterEdge) * 0.80
            + smoke * smokeMask * 0.75
            + halo * 0.55
            + flash * 0.45;
    a = clamp(a, 0.0, 1.0);

    // Consumed state: desaturate + dim.
    float consumed = 1.0 - uActive;
    float luma = dot(col, vec3(0.299, 0.587, 0.114));
    col = mix(col, vec3(luma), consumed * 0.5);
    a *= mix(0.35, 1.0, uActive);

    a *= smoothstep(0.0, 1.0, spawnS);

    gl_FragColor = vec4(col, a);
  }
`;

export function createHarbingerCircle(playerKey = 'p1') {
  const palette = PALETTES[playerKey] || PALETTES.p1;

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(12), 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([
    0, 0,
    1, 0,
    1, 1,
    0, 1,
  ]), 2));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);

  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTown:        { value: playerKey === 'town' ? 1 : 0 },
      uTime:        { value: 0 },
      uActive:      { value: 1 },
      uSpawn:       { value: 0 },
      uPlayFlash:   { value: 0 },
      uColorInner:  { value: palette.inner },
      uColorMid:    { value: palette.mid },
      uColorOuter:  { value: palette.outer },
      uColorGlow:   { value: palette.glow },
    },
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
  });

  const mesh = new THREE.Mesh(geometry, material);
  // renderOrder is rewritten every frame by GameBoard's
  // _syncHarbingerCircles based on view mode: -90 in surface (sits
  // below every card so the topmost stack layer covers it), and
  // 1.5 in subsurface (above the vanilla playmat overlay at
  // renderOrder=0.5 so the decal isn't painted over when the
  // subsurface skin reaches full opacity). The -90 default below
  // is used until the first render-tick update.
  mesh.renderOrder = -90;
  mesh.frustumCulled = false;
  mesh.userData = {
    playerKey,
    _harbingerActive: 1,
    _spawnedAt: performance.now(),
    _flashStartedAt: 0,
  };
  return mesh;
}

export function updateHarbingerCircleGeometry(mesh, cellGeo) {
  const pos = mesh.geometry.attributes.position.array;
  pos[0] = cellGeo.tl.x; pos[1]  = DECAL_Y; pos[2]  = cellGeo.tl.z;
  pos[3] = cellGeo.tr.x; pos[4]  = DECAL_Y; pos[5]  = cellGeo.tr.z;
  pos[6] = cellGeo.br.x; pos[7]  = DECAL_Y; pos[8]  = cellGeo.br.z;
  pos[9] = cellGeo.bl.x; pos[10] = DECAL_Y; pos[11] = cellGeo.bl.z;
  mesh.geometry.attributes.position.needsUpdate = true;
  mesh.geometry.computeBoundingSphere();
}

export function disposeHarbingerCircle(mesh) {
  mesh.geometry.dispose();
  mesh.material.dispose();
}
